import { ReactNode } from "react";
import { FormField, ResolvedFieldProps, SelectOption } from "@/lib/form-v2";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ContractTypeOptions,
  CommodityOptions,
  TradingAccount,
  generateAccountLabel,
  formulateOptionMonths,
} from "@/lib/api/otc";
import { Customer } from "@/lib/api/reports";
import { Trader } from "@/lib/api/reports";
import {
  OtcLayerFieldDef,
  OtcLayerFieldOptionsSource,
  getOtcLayerFieldDef,
} from "./fieldRegistry";
import { OtcLayerFormValues } from "./snapshotToForm";

export interface OtcLayerFieldOptionData {
  customers: Customer[];
  traders: Trader[];
  accounts: TradingAccount[];
}

function otcAccountOptions(accounts: TradingAccount[]): SelectOption[] {
  return accounts
    .filter((a: TradingAccount) => a.is_otc || a.is_futures)
    .map((a: TradingAccount) => ({
      value: a.id,
      label: generateAccountLabel(a.account_id) || a.account_id,
    }));
}

function htaAccountOptions(accounts: TradingAccount[]): SelectOption[] {
  return accounts
    .filter((a: TradingAccount) => a.is_futures || false)
    .map((a: TradingAccount) => ({
      value: a.id,
      label: generateAccountLabel(a.account_id) || a.account_id,
    }));
}

/**
 * Resolves the option list for a select field from its registry
 * `optionsSource`. Option-month combos derive their options from the field's
 * own current value (matching v2 behaviour); list-backed sources come from the
 * caller-provided customer/trader/account data.
 */
function resolveOptions(
  source: OtcLayerFieldOptionsSource,
  currentValue: unknown,
  data: OtcLayerFieldOptionData,
): SelectOption[] {
  const currentString =
    typeof currentValue === "string" ? currentValue : undefined;

  switch (source) {
    case "contract_type":
      return ContractTypeOptions;
    case "commodity":
      return CommodityOptions;
    case "customer":
      return data.customers.map((c: Customer) => ({
        value: c.id,
        label: c.long_name,
      }));
    case "trader":
      return data.traders.map((t: Trader) => ({
        value: t.id,
        label: t.name || "",
      }));
    case "otc_account":
      return otcAccountOptions(data.accounts);
    case "hta_account":
      return htaAccountOptions(data.accounts);
    case "option_month":
    case "rolled_option_month":
    case "rolled_basis_month":
    case "option_month_etc_one":
      return formulateOptionMonths(currentString);
    default:
      return [];
  }
}

interface OtcLayerFieldRendererProps {
  field_name: string;
  values: OtcLayerFormValues;
  optionData: OtcLayerFieldOptionData;
  disabled: boolean;
  /** Whether the field is rendered with a "required" indicator. */
  required?: boolean;
  /** Validation error text, if any (chunked in later validation work). */
  error?: string;
  showError?: boolean;
  /** Orange warning treatment for non-validation review warnings. */
  warning?: boolean;
  /** Extra adornment rendered in the field label (e.g. a warning icon). */
  extra?: ReactNode;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
}

/**
 * Read-only display cell for a derived (backend-computed) field value.
 * Renders a disabled input that visually matches the other form fields.
 * Used in both the editable form (live preview of derived math) and the
 * read-only layer card.
 */
export function DerivedValueCell({
  label,
  value,
}: {
  label: string;
  value: string | undefined | null;
}) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="grid items-center gap-1.5">
      <Label className="font-semibold text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        type="text"
        disabled
        readOnly
        value={value}
        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 disabled:cursor-default disabled:opacity-70"
      />
    </div>
  );
}

/**
 * Renders a single owned layer field through the low-level `FormField` widget.
 *
 * This is the layer-native counterpart to the v2 form renderer. It does not
 * consult v2 schemas or `buildFieldMeta`; the field's widget/label come from
 * the v3 registry, and editability is decided by the caller (backend
 * `owned_fields`). Unknown/derived fields (not in the registry) render nothing.
 */
export function OtcLayerFieldRenderer({
  field_name,
  values,
  optionData,
  disabled,
  required,
  error,
  showError,
  warning,
  extra,
  onChange,
  onBlur,
}: OtcLayerFieldRendererProps) {
  const def: OtcLayerFieldDef | undefined = getOtcLayerFieldDef(field_name);
  if (!def) return null;

  const value = values[field_name];
  const options =
    def.widget === "select" && def.optionsSource
      ? resolveOptions(def.optionsSource, value, optionData)
      : [];

  const fieldProps: ResolvedFieldProps = {
    name: field_name,
    label: def.label,
    widget: def.widget,
    required: required ?? false,
    disabled,
    visible: true,
    step: def.step,
    options,
    value,
    error,
    errorKind: showError ? "format" : undefined,
    showError: showError ?? false,
  };

  return (
    <FormField
      fieldProps={fieldProps}
      onChange={onChange}
      onBlur={onBlur}
      warning={warning}
      extra={extra}
    />
  );
}
